import Heading from "@ui-kit/typography/Heading/Heading.web";
import useWindowSize from "@shared/hooks/useWindowSize";
import InventoryBadge from "@ui-application/data-display/badges/InventoryBadge/InventoryBadge.web";
import SaleStartsOnBadge from "@ui-application/data-display/badges/SaleStartsOnBadge/SaleStartsOnBadge.web";
import StatusBadge from "@ui-application/data-display/badges/StatusBadge/StatusBadge.web";
import QuantitySelect from "@ui-application/form/QuantitySelect/QuantitySelect.web";
import CurrencyFormat from "@ui-kit/data-display/CurrencyFormat/CurrencyFormat.web";
import AllInPricing from "@shared/modules/financials/components/AllInPricing/AllInPricing.web";
import ItemSelectVerbiage from "@shared/modules/seating/components/ItemSelectVerbiage/ItemSelectVerbiage.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import Card from "@ui-kit/data-display/Card/Card.web";
import { getItemPrice } from "@shared/modules/financials/utils/price-utils";
import { useSeatingState } from "@shared/modules/seating/contexts/useSeatingState";
import { isOnSaleTicketType } from "@shared/modules/ticket-types/utils/ticket-type-utils";
import breakpoints from "@shared/theme/props/breakpoints";
import { useTranslation } from "@shared/utils/internationalization";
import { useMemo } from "react";
import useDjangoWaffle from "@shared/hooks/djangoWaffle/useDjangoWaffle";
import PWYCTicketType from "@shared/modules/events/components/tickets/pay-what-you-can/PWYCTicketType/PWYCTicketType.web";
import { DefaultSeatSelectProps } from "./DefaultSeatSelect.props";
import { useDefaultSeatSelect } from "./useDefaultSeatSelect";

const { sm } = breakpoints;

const DefaultSeatSelect = (props: DefaultSeatSelectProps) => {
  const {
    item,
    venueData,
    voucherData,
    ticketAuthArray,
    updateTicketAuthArray,
    handleScreen,
    areAllRenewalItemGroups,
  } = props;

  const { FLAGS } = useDjangoWaffle();
  const { t } = useTranslation("seating");

  const { width: windowWidth } = useWindowSize();
  const isSmallerThanSm = useMemo(
    () => Boolean(windowWidth && windowWidth < sm - 1),
    [windowWidth]
  );

  const { seatingEventData } = useSeatingState();

  const {
    isSoldOut,
    isDisabledSelector,
    showButtons,
    purchaseLimits,
    purchaseSourcePlatform,
    paymentType,
    selectItemCallback,
    pwycPrice,
    handleSetPwycPrice,
    currentPwycPrice,
    pwycConfig,
  } = useDefaultSeatSelect(props);

  return (
    <Card
      key={item.id}
      p="md"
      w="full"
      border="1px solid"
      borderColor="transparent"
      rounded={{ base: "lg", sm: "lg" }}
      display="flex"
      flexDirection="column"
      gap="xs"
    >
      {/* Controls display of inventory badges, sold out badges, etc. */}
      <Flex alignItems="center" gap="xs">
        {/* Sale starts on badge */}
        <SaleStartsOnBadge
          saleStartsOn={item.sale_starts_on}
          isOnSale={isOnSaleTicketType(item)}
          timezone={seatingEventData?.timezone}
        />
        {/* Low inventory / remaining inventory badge */}
        {seatingEventData && (
          <InventoryBadge
            tt={item}
            eventData={seatingEventData}
            venueData={venueData}
          />
        )}
        {/* Sold out badge */}
        {isSoldOut && (
          <StatusBadge status="error">
            <Text fontSize="xs" textTransform="uppercase">
              {t("Sold Out", { ns: "common" })}
            </Text>
          </StatusBadge>
        )}
      </Flex>
      <Flex
        w="full"
        direction={{ base: "column", md: "row" }}
        opacity={isSoldOut ? "0.3" : "1.0"}
      >
        <Box width="100%" maxWidth="100%">
          {/* Show item name and color */}
          <Flex width="100%" maxWidth="100%" alignItems="center">
            <Text
              mt="2xs"
              w="element.sm"
              h="element.sm"
              borderRadius="50%"
              mr="sm"
              style={{
                background: item.colorFill,
                position: "relative",
              }}
            />
            <Heading size="2xl">{item.name}</Heading>
          </Flex>

          {/* Show item-level purchase limit when the value isn't overridden */}
          {item.minimum_purchase_limit &&
            !purchaseLimits?.multiItemsAllowed && (
              <Flex mt="md">
                <Text color="secondary.400">{`*${t(
                  "Minimum {{count}} items required",
                  {
                    count: item.minimum_purchase_limit,
                  }
                )}`}</Text>
              </Flex>
            )}

          {pwycConfig &&
          item?.price &&
          showButtons &&
          FLAGS?.use_pwyc_configuration ? (
            <PWYCTicketType
              defaultPrice={getItemPrice(
                item?.price,
                venueData.all_in_pricing_is_enabled,
                undefined,
                paymentType,
                purchaseSourcePlatform
              )}
              pwycConfig={pwycConfig}
              currency={venueData.currency}
              isWaitlisted={false}
              quantityButtonsComponent={
                <QuantitySelect
                  isDisabled={isDisabledSelector}
                  buttonProps={{
                    showButton: true,
                    callbackFunc: selectItemCallback,
                  }}
                />
              }
              price={pwycPrice ?? currentPwycPrice}
              onSetPrice={handleSetPwycPrice}
            />
          ) : (
            <>
              {/* Show item price + all in pricing breakdown */}
              <Flex mt="md" alignItems="center" justifyContent="space-between">
                <Flex direction="column">
                  {venueData.currency &&
                    item.price &&
                    item.fees_pricing_info && (
                      <Heading as="h3" size="lg">
                        <CurrencyFormat
                          value={getItemPrice(
                            item?.price?.toString(),
                            venueData.all_in_pricing_is_enabled,
                            item?.fees_pricing_info,
                            paymentType,
                            purchaseSourcePlatform
                          )}
                          displayType="text"
                          currency={venueData.currency}
                          includeSuffix
                        />
                      </Heading>
                    )}
                  <AllInPricing
                    feesPricingInfo={item?.fees_pricing_info}
                    hasAllInPricing={venueData.all_in_pricing_is_enabled}
                    price={item?.price?.toString()}
                    currency={venueData.currency}
                    purchaseSourcePlatform={purchaseSourcePlatform}
                    paymentType={paymentType}
                  />
                </Flex>

                {/* Show quantity button/selection dropdown on same line as price */}
                {!isSmallerThanSm && showButtons && (
                  <>
                    <Box minWidth="element.2xl" />
                    <Flex
                      width="element.9xl"
                      direction="row"
                      justifyContent="flex-end"
                    >
                      <QuantitySelect
                        isDisabled={isDisabledSelector}
                        buttonProps={{
                          showButton: true,
                          callbackFunc: () => {
                            selectItemCallback();
                            if (areAllRenewalItemGroups && handleScreen) {
                              handleScreen("select-customer");
                            }
                          },
                        }}
                      />
                    </Flex>
                  </>
                )}
              </Flex>

              {/* Show quantity button/selection dropdown on a new line */}
              {isSmallerThanSm && showButtons && (
                <Flex
                  pt="lg"
                  width="100%"
                  direction="row"
                  justifyContent="flex-end"
                >
                  <QuantitySelect
                    isDisabled={isDisabledSelector}
                    buttonProps={{
                      showButton: true,
                      callbackFunc: () => {
                        selectItemCallback();
                        if (areAllRenewalItemGroups && handleScreen) {
                          handleScreen("select-customer");
                        }
                      },
                    }}
                  />
                </Flex>
              )}
            </>
          )}
        </Box>
      </Flex>
      {/* Shows verbiage for the item's payment plans, password entry form, and readmore description */}
      <ItemSelectVerbiage
        item={item}
        voucherData={voucherData}
        venueData={venueData}
        ticketAuthArray={ticketAuthArray}
        updateTicketAuthArray={updateTicketAuthArray}
      />
    </Card>
  );
};

export default DefaultSeatSelect;
